import React, { useContext, useEffect, useState } from "react";
import { FormControl, InputLabel, Select, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";

import { SessionContext } from "../../../utility/useSession";
import { useDate } from "../../../utility/useDate";
import { api, IKmosAllocationDashLane, IKmosAllocationDashResult, KmosAllocationType } from "../../../logic/api";
import { LaneLabel } from "./components/LaneLabel";
import styles from "./LanesByTimeSlot.module.css";

enum LaneFilter {
  ALL,
  OPEN,
  CLOSED,
}

interface LanesByTimeSlotContentProps {
  startDate: Date;
  setStartDate: (date: Date) => void;
  daysToDisplay: number;
}

const LanesByTimeSlotContent: React.FunctionComponent<LanesByTimeSlotContentProps> = ({
  startDate
}) => {
  const { t } = useTranslation();
  const session = useContext(SessionContext);
  const { localizeDate } = useDate();

  const [laneFilter, setLaneFilter] = useState<LaneFilter>(LaneFilter.ALL);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [allocations, setAllocations] = useState<IKmosAllocationDashResult[]>([]);

  const getAllAllocationsAPI = api.hooks.kmosV2.getAllocationsByDate();

  // Fetch allocations
  useEffect(() => {
    if (!startDate) return;

    setIsLoading(true);
    getAllAllocationsAPI.send(
      session.current.context,
      {
        date: localizeDate(startDate).toFormat("yyyy-MM-dd"),
        kmosAllocationType_notIn: [KmosAllocationType.suggested]
      },
      (allocationData) => {
        if (allocationData) {
          setAllocations(allocationData);
        }
        setIsLoading(false);
      }
    );
  }, [startDate, session]);

  // Get unique dates from allocations
  const uniqueDates = [...new Set(allocations.map(a => a.allocationDate))].sort();

  // Get allocations for a specific date
  const getAllocationsForDate = (date: string) => {
    return allocations.filter(a => a.allocationDate === date);
  };

  const filterLanes = (allocations: IKmosAllocationDashLane[] | undefined) => {
    if (!allocations) return [];

    switch (laneFilter) {
      case LaneFilter.OPEN:
        return allocations.filter(lane => lane.active);
      case LaneFilter.CLOSED:
        return allocations.filter(lane => !lane.active);
      case LaneFilter.ALL:
      default:
        return allocations;
    }
  };

  return (
    <div className={styles.lanesContent}>
      {/* Controls */}
      <div className={styles.filterContainer}>
        <FormControl variant="outlined" size="small" sx={{ minWidth: "150px", mr: 2 }}>
          <InputLabel id="lane-filter-label">{t("kmos.labels.filterBy")}</InputLabel>
          <Select
            labelId="lane-filter-label"
            value={laneFilter}
            onChange={(e) => setLaneFilter(e.target.value as LaneFilter)}
            label={t("kmos.labels.filterBy")}
          >
            <MenuItem value={LaneFilter.ALL}>{t("kmos.labels.allLanes")}</MenuItem>
            <MenuItem value={LaneFilter.OPEN}>{t("kmos.labels.openLanes")}</MenuItem>
            <MenuItem value={LaneFilter.CLOSED}>{t("kmos.labels.closedLanes")}</MenuItem>
          </Select>
        </FormControl>
      </div>

      {/* Date group section */}
      <div className={styles.dateSections}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <CircularProgress size={24} className={styles.loadingSpinner} />
            <span>{t("general.loading")}</span>
          </div>
        ) : (
          uniqueDates.map((dateStr) => {
            // Convert string date to Date object for localization
            const date = new Date(dateStr);
            const displayDate = localizeDate(date).toFormat("EEE, MMM dd, yyyy");
            const dateAllocations = getAllocationsForDate(dateStr);

            return (
              <div key={dateStr} className={styles.dateGroup}>
                <div className={styles.stickyDateHeader}>
                  {displayDate}
                </div>

                <TableContainer component={Paper} className={styles.tableContainer}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ fontWeight: "bold" }} className={`${styles.subHeader} ${styles.timeSlotHeaderCell}`}>
                          {t("kmos.labels.timeSlot")}
                        </TableCell>
                        <TableCell style={{ fontWeight: "bold" }} className={styles.subHeader}>
                          {t("kmos.labels.lanes")}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dateAllocations.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={2} align="center" className={styles.noAllocations}>
                            {t("kmos.labels.noTimeSlots")}
                          </TableCell>
                        </TableRow>
                      ) : (
                        dateAllocations.map((allocation) => {
                          const filteredLanes = filterLanes(allocation.lanes);

                          return (
                            <TableRow key={`${allocation.allocationDate}-${allocation.letter}`} hover>
                              <TableCell className={styles.timeSlotCell}>
                                <div className={styles.timeSlotTitle}>
                                  <strong>{`${allocation.startTime} - ${allocation.endTime}`}</strong>
                                  <div className={styles.timeSlotLetter}>{allocation.letter}</div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className={styles.laneGrid}>
                                  {filteredLanes.length === 0 ? (
                                    <span className={styles.noAllocations}>{t("input.validations.noLanesAllocated")}</span>
                                  ) : (
                                    filteredLanes.map((lane) => (
                                      <LaneLabel
                                        key={lane.id}
                                        id={lane.id}
                                        zoneName={lane.parentName}
                                        laneName={lane.name}
                                        capacity={lane.capacity}
                                        backgroundColor={lane.color}
                                        isActive={lane.active}
                                        variant="compact"
                                      />
                                    ))
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default LanesByTimeSlotContent;
